import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Alert } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Building2, Calendar, CheckCircle, ChevronDown, ChevronUp, Clock, Download, Info, Mail, Phone } from 'lucide-react';
import { cleanRepeatedText, cn, downloadPdfFile, formatDateTime, truncateText } from '../../lib/utils';
import { captureEvent } from '../../lib/analytics/posthog';
import {
  getPartySignerGuid,
  getSignerTypeDisplay,
  getSigningPartyStatusBadge,
  parseSigningPartyRegistryStatus,
} from '../../lib/documentHelpers';

interface DocumentMetadataPanelProps {
  documentInfo: any | null;
  signerId: string;
  authLoading: boolean;
  hasQueryParams: boolean;
  /** On mobile, title and status are shown at the top of the details tab */
  compact?: boolean;
  isMobile?: boolean;
  pdfUrl?: string | null;
  isPdfLoading?: boolean;
}

const DocumentMetadataPanel: React.FC<DocumentMetadataPanelProps> = ({
  documentInfo,
  signerId,
  authLoading,
  hasQueryParams,
  compact = false,
  isMobile = false,
  pdfUrl = null,
  isPdfLoading = false,
}) => {
  const { t } = useLanguage();
  const [showMoreDetails, setShowMoreDetails] = useState(false);

  const handleDownload = () => {
    if (!pdfUrl) return;
    captureEvent('document_pdf_download_clicked', { surface: 'details' });
    downloadPdfFile(pdfUrl, documentInfo?.title);
  };

  const downloadButton = (
    <Button
      type="button"
      variant="outline"
      className="w-full disabled:opacity-50"
      onClick={handleDownload}
      disabled={!pdfUrl || isPdfLoading}
    >
      {isPdfLoading ? (
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      ) : (
        <Download className="h-4 w-4" />
      )}
      {t('downloadDocument')}
    </Button>
  );

  if (!documentInfo) {
    if (authLoading && hasQueryParams) {
      return (
        <div className="flex justify-center py-8">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      );
    }
    return <Alert>{t('noDocumentInfo')}</Alert>;
  }

  const metadataContent = (
    <div className="space-y-3 text-sm">
      {compact && downloadButton}

      {!compact && (
        <div className="flex items-center gap-2">
          {documentInfo.status ? (
            <CheckCircle className="h-4 w-4 text-green-600" aria-hidden />
          ) : (
            <Clock className="h-4 w-4 text-amber-600" aria-hidden />
          )}
          <Badge variant={documentInfo.status ? 'success' : 'warning'}>
            {documentInfo.status ? t('documentStatusDone') : t('documentStatusInProgress')}
          </Badge>
        </div>
      )}

      {!compact && downloadButton}

      {documentInfo.description && (
        <p className="text-muted-foreground">
          {truncateText(cleanRepeatedText(documentInfo.description), isMobile ? 200 : 200)}
        </p>
      )}
      {documentInfo.summary && (
        <p>
          <strong>{t('summary')}:</strong> {documentInfo.summary}
        </p>
      )}
      {(documentInfo.validFrom || documentInfo.validTo) && (
        <div className="space-y-1">
          {documentInfo.validFrom && (
            <p className="flex items-center gap-2">
              <Calendar className="h-4 w-4 shrink-0" />
              <strong>{t('from')}:</strong> {formatDateTime(documentInfo.validFrom)}
            </p>
          )}
          {documentInfo.validTo && (
            <p className="flex items-center gap-2">
              <Calendar className="h-4 w-4 shrink-0" />
              <strong>{t('to')}:</strong> {formatDateTime(documentInfo.validTo)}
            </p>
          )}
        </div>
      )}
      {(documentInfo.organisationName || documentInfo.organisationTIN) && !isMobile && (
        <p className="flex items-center gap-2">
          <Building2 className="h-4 w-4 shrink-0" />
          {documentInfo.organisationTIN && `${documentInfo.organisationTIN} - `}
          {documentInfo.organisationName || t('notAvailable')}
        </p>
      )}

      {compact && documentInfo.operationId && (
        <div>
          <button
            type="button"
            className="flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm font-medium"
            onClick={() => {
              setShowMoreDetails((v) => {
                const next = !v;
                captureEvent('document_more_details_toggled', { expanded: next });
                return next;
              });
            }}
            aria-expanded={showMoreDetails}
          >
            {t('documentMoreDetails')}
            {showMoreDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {showMoreDetails && (
            <p className="mt-2 flex items-start gap-2 break-all text-muted-foreground">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                <strong>{t('operationId')}:</strong> {documentInfo.operationId}
              </span>
            </p>
          )}
        </div>
      )}

      {!compact && documentInfo.operationId && (
        <p className="flex items-center gap-2 break-all">
          <Info className="h-4 w-4 shrink-0" />
          {documentInfo.operationId}
        </p>
      )}

      {(documentInfo.SignParties ?? documentInfo.signParties)?.length > 0 && (
        <div className="space-y-2">
          <p className="font-semibold">{t('signingParties')}</p>
          {(documentInfo.SignParties ?? documentInfo.signParties).map((signer: any, index: number) => {
            const sid = signerId.trim();
            const partyId = getPartySignerGuid(signer);
            const isSelectedParty = sid.length > 0 && partyId === sid;
            const typeLabel = getSignerTypeDisplay(signer, t);
            const partyStatusBadge = getSigningPartyStatusBadge(
              parseSigningPartyRegistryStatus(signer),
              t,
            );
            return (
              <div
                key={partyId || index}
                className={cn(
                  'rounded-md border p-3 text-sm transition-colors',
                  isSelectedParty
                    ? 'border-primary bg-primary/5 ring-2 ring-primary/25'
                    : 'border-border',
                )}
                aria-current={isSelectedParty ? 'true' : undefined}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="min-w-0 flex-1 font-medium">
                    {signer.name} {signer.surname}
                  </p>
                  {partyStatusBadge && (
                    <Badge variant={partyStatusBadge.variant} className="shrink-0">
                      {partyStatusBadge.label}
                    </Badge>
                  )}
                </div>
                {typeLabel && (
                  <p className="mt-0.5 text-muted-foreground">
                    <span className="font-medium text-foreground/80">{t('signerRole')}:</span>{' '}
                    {typeLabel}
                  </p>
                )}
                {signer.email && (
                  <p className="flex items-center gap-1.5 text-muted-foreground" title={signer.email}>
                    <Mail className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{signer.email}</span>
                  </p>
                )}
                {signer.fin && (
                  <p className="flex items-center gap-1.5 text-muted-foreground">
                    <Info className="h-3.5 w-3.5 shrink-0" />
                    {t('finLabel')}: {signer.fin}
                  </p>
                )}
                {signer.phone && (
                  <p className="flex items-center gap-1.5 text-muted-foreground">
                    <Phone className="h-3.5 w-3.5 shrink-0" />
                    {signer.phone}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  if (compact) {
    return metadataContent;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="line-clamp-2">
          {cleanRepeatedText(documentInfo.title) || t('document')}
        </CardTitle>
      </CardHeader>
      <CardContent>{metadataContent}</CardContent>
    </Card>
  );
};

export default DocumentMetadataPanel;
